import { AudioDeviceOption, isVirtualInputLabel, pickDefaultInput } from "@swivl-mono/chat-ui";
import { CreateLogger } from "@swivl-mono/helpers";
import { Log } from "@swivl-mono/types";
import { Device } from "@twilio/voice-sdk";

const log = CreateLogger(Log.VOICE, "CCAudioDevices");

/**
 * Agent audio devices — which microphone and speaker the Contact Center uses,
 * and whether the microphone is actually producing sound.
 *
 * ── Why this exists ──────────────────────────────────────────────────────
 * Chuck lost an evening to "the caller can't hear the agent". Measured in the
 * browser, the cause was:
 *
 *     defaultCaptureLabel: "BlackHole 16ch (Virtual)"
 *     trackEnabled: true, trackMuted: false, peakAmplitude: 0
 *
 * Chrome's default input was a virtual loopback device with nothing routed into
 * it, so `getUserMedia({audio: true})` — exactly what the Voice SDK does —
 * captured it and published pure silence. EVERY signal said healthy: the
 * browser mic indicator was lit (a device WAS being captured), no SDK errors
 * fired (nothing failed), Twilio reported an unmuted participant in a live
 * conference (it was one), and the agent could hear the caller (receiving is
 * unaffected). The only wrong thing was the CONTENT of the audio, which nothing
 * measured.
 *
 * An agent would not experience that as a configuration problem. They would
 * experience it as callers hanging up on them.
 *
 * So this module owns three things the CC previously left to chance:
 *   1. an explicit device choice, remembered per agent;
 *   2. a default that prefers a real input over a virtual one;
 *   3. a live level measurement, so silence is visible rather than inferred.
 */

const STORAGE_KEY_INPUT = "cc.audio.inputDeviceId";
const STORAGE_KEY_OUTPUT = "cc.audio.outputDeviceId";

export class CCAudioDevices {
  /** Live level monitoring state, torn down with `stopMeter`. */
  private static meterContext: AudioContext | null = null;
  private static meterStream: MediaStream | null = null;
  private static meterFrame: number | null = null;

  static get savedInputId(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY_INPUT);
    } catch {
      return null;
    }
  }

  static get savedOutputId(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY_OUTPUT);
    } catch {
      return null;
    }
  }

  /**
   * List audio devices. Labels are hidden until microphone permission has been
   * granted, so this asks for it first — an unlabelled list is useless for
   * choosing, and it is what let a virtual device hide in plain sight.
   */
  static async list(): Promise<{ inputs: AudioDeviceOption[]; outputs: AudioDeviceOption[] }> {
    // Only ask for the microphone if labels are actually hidden. Probing
    // unconditionally lights the browser's recording indicator every time the
    // panel mounts, which on an agent console is alarming and pointless.
    let devices = await navigator.mediaDevices.enumerateDevices();
    const labelsHidden = devices.filter((d) => d.kind === "audioinput").every((d) => !d.label);
    if (labelsHidden) {
      try {
        const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
        probe.getTracks().forEach((t) => t.stop());
        devices = await navigator.mediaDevices.enumerateDevices();
      } catch (err) {
        log("microphone permission not granted — device labels will be hidden", err);
      }
    }
    const toOption = (d: MediaDeviceInfo): AudioDeviceOption => ({
      deviceId: d.deviceId,
      label: d.label || (d.kind === "audioinput" ? "Microphone" : "Speaker"),
      isVirtual: isVirtualInputLabel(d.label || "")
    });
    return {
      inputs: devices.filter((d) => d.kind === "audioinput").map(toOption),
      outputs: devices.filter((d) => d.kind === "audiooutput").map(toOption)
    };
  }

  /** Apply an input device to the Twilio Device and remember it. */
  static async setInput(device: Device | null, deviceId: string): Promise<void> {
    try {
      localStorage.setItem(STORAGE_KEY_INPUT, deviceId);
    } catch {
      /* private browsing — the choice just will not persist */
    }
    if (!device) return;
    try {
      await device.audio?.setInputDevice(deviceId);
      log(`input device set to ${deviceId}`);
    } catch (err) {
      log("failed to set input device", err);
    }
  }

  /** Apply an output device to the Twilio Device and remember it. */
  static async setOutput(device: Device | null, deviceId: string): Promise<void> {
    try {
      localStorage.setItem(STORAGE_KEY_OUTPUT, deviceId);
    } catch {
      /* private browsing */
    }
    if (!device) return;
    try {
      await device.audio?.speakerDevices.set(deviceId);
      log(`output device set to ${deviceId}`);
    } catch (err) {
      log("failed to set output device", err);
    }
  }

  /**
   * Apply the remembered devices, or a sane default, when the Device starts.
   * Returns the input actually selected so the caller can surface it.
   */
  static async applySaved(device: Device | null): Promise<string | undefined> {
    const { inputs, outputs } = await CCAudioDevices.list();
    const input = pickDefaultInput(inputs, CCAudioDevices.savedInputId);
    if (input) await CCAudioDevices.setInput(device, input);

    const savedOutput = CCAudioDevices.savedOutputId;
    if (savedOutput && outputs.some((o) => o.deviceId === savedOutput)) {
      await CCAudioDevices.setOutput(device, savedOutput);
    }

    return input;
  }

  /**
   * Start measuring the selected microphone's level. `onLevel` receives a
   * 0..1 peak roughly 20 times a second. Returns false if the mic could not be
   * opened at all.
   *
   * This is the piece that makes a dead microphone visible: an agent should
   * never have to infer it from callers behaving oddly.
   */
  static get meterRunning(): boolean {
    return !!CCAudioDevices.meterStream;
  }

  static async startMeter(deviceId: string | undefined, onLevel: (level: number) => void): Promise<boolean> {
    await CCAudioDevices.stopMeter();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId ? { deviceId: { exact: deviceId } } : true
      });
      const ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);

      const buffer = new Float32Array(analyser.fftSize);
      CCAudioDevices.meterStream = stream;
      CCAudioDevices.meterContext = ctx;

      const tick = (): void => {
        analyser.getFloatTimeDomainData(buffer);
        let peak = 0;
        for (const v of buffer) peak = Math.max(peak, Math.abs(v));
        onLevel(peak);
        CCAudioDevices.meterFrame = window.setTimeout(tick, 50);
      };
      tick();
      return true;
    } catch (err) {
      log("could not start the input meter", err);
      return false;
    }
  }

  static async stopMeter(): Promise<void> {
    if (CCAudioDevices.meterFrame !== null) {
      clearTimeout(CCAudioDevices.meterFrame);
      CCAudioDevices.meterFrame = null;
    }
    CCAudioDevices.meterStream?.getTracks().forEach((t) => t.stop());
    CCAudioDevices.meterStream = null;
    if (CCAudioDevices.meterContext) {
      await CCAudioDevices.meterContext.close().catch(() => {});
      CCAudioDevices.meterContext = null;
    }
  }
}
